import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { expenseSchema, type ExpenseFormData } from '@/lib/validators';
import { useExpenses } from '@/composables/expense/useExpenses';
import { useExpenseCategories } from '@/composables/expense/useExpenseCategories';
import { useAccounts } from '@/composables/financial/useAccounts';
import { formatCurrency } from '@/lib/formatters';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { FileUploader } from '@/components/FileUploader';
import { CURRENCIES } from '@/lib/constants';
import { useState, useRef } from 'react';

export function ExpenseCreatePage() {
  const navigate = useNavigate();
  const { createExpense, uploadAttachment } = useExpenses();
  const { categories } = useExpenseCategories();
  const { accounts } = useAccounts();
  const form = useForm<ExpenseFormData>({ resolver: zodResolver(expenseSchema), defaultValues: { title: '', description: '', amount: 0, currency: 'USD', expenseDate: '', categoryId: '', accountId: '', merchantName: '', receiptNumber: '' } });
  const pendingFiles = useRef<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: ExpenseFormData) => {
    setIsSubmitting(true);
    createExpense.mutate(data, {
      onSuccess: async (result) => {
        const expenseId = result?.data?.id;
        if (expenseId && pendingFiles.current.length > 0) {
          for (const file of pendingFiles.current) {
            uploadAttachment.mutate({ id: expenseId, file });
          }
        }
        navigate('/expenses');
      },
      onSettled: () => setIsSubmitting(false),
    });
  };

  return (
    <PageContainer backTo title="Create Expense">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Expense Info</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormField form={form} name="title" label="Title" required /><FormCurrencyInput form={form} name="amount" label="Amount" required /><FormSelect form={form} name="currency" label="Currency" options={CURRENCIES.map(c => ({ label: c, value: c }))} /><FormDatePicker form={form} name="expenseDate" label="Expense Date" required /><FormSelect form={form} name="categoryId" label="Category" required options={categories.map(c => ({ label: c.name, value: c.id }))} />
            <FormSelect form={form} name="accountId" label="Deduct from Account" options={accounts.map(a => ({ label: `${a.name} (${formatCurrency(a.balance, a.currency)})`, value: a.id }))} />
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Additional</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormField form={form} name="merchantName" label="Merchant" /><FormField form={form} name="receiptNumber" label="Receipt Number" /><FormTextarea form={form} name="description" label="Description" />
            <div><p className="text-sm font-medium mb-2">Attachments</p><FileUploader onFilesSelected={(files) => { pendingFiles.current = [...pendingFiles.current, ...files]; }} accept="image/*,.pdf" multiple /></div>
          </CardContent></Card>
        </div>
        <div className="mt-6 flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button><Button type="submit" disabled={isSubmitting || createExpense.isPending}>{isSubmitting || createExpense.isPending ? 'Creating...' : 'Create Expense'}</Button></div>
      </form>
    </PageContainer>
  );
}
